/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery, useMutation } from '@apollo/client'
import React from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  DETECTION_IMAGE,
  DetectionImageQuery,
  DetectionImageQueryVariables,
  DetectionImageSession,
  DetectionLeaseLockError,
  NEXT_IMAGE_FOR_DETECTION,
  NextImageForDetectionQuery,
  RELEASE_IMAGE_LEASE,
  ReleaseImageLeaseMutation,
  ReleaseImageLeaseMutationVariables,
} from '@/graphql'

const shouldHideStatusMessage = (message: string | null) => {
  if (!message) return false
  const normalizedMessage = message.trim().toLowerCase()

  return (
    normalizedMessage === 'using temporary mock detection data.' ||
    normalizedMessage.includes('temporary mock detection data')
  )
}

export function useDetectionSession(
  initialSession: DetectionImageSession | null = null,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isAdvancing, setIsAdvancing] = React.useState(false)
  const [injectedSession, setInjectedSession] = React.useState(initialSession)
  const activeImageKeyRef = React.useRef<string | null>(null)
  const activeImageCanEditRef = React.useRef(false)
  const handledImageIdsRef = React.useRef(new Set<string>())
  const skipEffectImageIdRef = React.useRef<string | null>(null)
  const requestedImageId = searchParams.get('imageId')

  const [
    loadNextImageQuery,
    { data: nextData, loading: loadingNext, error: nextError },
  ] = useLazyQuery<NextImageForDetectionQuery, Record<string, never>>(
    NEXT_IMAGE_FOR_DETECTION,
    {
      context: { endPoint: 'mlUltra' },
      fetchPolicy: 'network-only',
    },
  )

  const [
    loadDetectionImage,
    { data: detectionData, loading: loadingImage, error: detectionError },
  ] = useLazyQuery<DetectionImageQuery, DetectionImageQueryVariables>(
    DETECTION_IMAGE,
    {
      context: { endPoint: 'mlUltra' },
      fetchPolicy: 'network-only',
    },
  )

  const [releaseImageLease] = useMutation<
    ReleaseImageLeaseMutation,
    ReleaseImageLeaseMutationVariables
  >(RELEASE_IMAGE_LEASE, {
    context: { endPoint: 'mlUltra' },
  })

  const nextResult = nextData?.nextImageForDetection
  const sessionResult = detectionData?.detectionImage
  const fetchedSession =
    sessionResult?.__typename === 'DetectionImageSession' ? sessionResult : null
  const session = injectedSession ?? fetchedSession
  const image = session?.image ?? null
  const canEdit = session?.canEdit ?? false
  const reviewFeedback = session?.reviewFeedback ?? null
  const returnedFeedbackCount = session?.returnedFeedbackCount ?? 0
  const statusMessage = shouldHideStatusMessage(session?.statusMessage ?? null)
    ? null
    : session?.statusMessage ?? null
  const noItems =
    sessionResult?.__typename === 'NoItemsAvailableError'
      ? sessionResult
      : nextResult?.__typename === 'NoItemsAvailableError'
        ? nextResult
        : null
  const leaseLock =
    nextResult?.__typename === 'LeaseLockError'
      ? (nextResult as DetectionLeaseLockError)
      : null
  const loading = isAdvancing || loadingNext || loadingImage
  const error = nextError || detectionError

  const requestDetectionImage = React.useCallback(
    async (imageId: string) => {
      if (!imageId) return
      try {
        await loadDetectionImage({ variables: { imageId } })
      } catch (loadError) {
        console.error('Failed to load detection image', loadError)
      }
    },
    [loadDetectionImage],
  )

  const requestNextImage = React.useCallback(async () => {
    if (injectedSession) return null
    setIsAdvancing(true)
    try {
      const response = await loadNextImageQuery()
      const result = response.data?.nextImageForDetection
      if (result?.__typename === 'ImagePointer') {
        skipEffectImageIdRef.current = result.imageId
        const next = new URLSearchParams(searchParams)
        next.set('imageId', result.imageId)
        setSearchParams(next, { replace: true })
        await requestDetectionImage(result.imageId)
      }
      return result
    } finally {
      setIsAdvancing(false)
    }
  }, [
    injectedSession,
    loadNextImageQuery,
    requestDetectionImage,
    searchParams,
    setSearchParams,
  ])

  const releaseLeaseForImage = React.useCallback(
    async (imageId: string) => {
      if (!imageId) return false
      try {
        await releaseImageLease({ variables: { imageId } })
      } catch (releaseError) {
        console.error('Failed to release image lease', releaseError)
        return false
      }
    },
    [releaseImageLease],
  )

  React.useEffect(() => {
    setInjectedSession(initialSession)
  }, [initialSession])

  React.useEffect(() => {
    if (injectedSession) {
      return
    }
    if (requestedImageId) {
      if (skipEffectImageIdRef.current === requestedImageId) {
        skipEffectImageIdRef.current = null
        return
      }
      void requestDetectionImage(requestedImageId)
      return
    }
    void requestNextImage()
  }, [
    injectedSession,
    requestDetectionImage,
    requestNextImage,
    requestedImageId,
  ])

  React.useEffect(() => {
    const nextImageId = image?.id ?? null
    const previousImageId = activeImageKeyRef.current
    const previousCanEdit = activeImageCanEditRef.current

    if (
      previousImageId &&
      previousImageId !== nextImageId &&
      previousCanEdit &&
      !handledImageIdsRef.current.has(previousImageId)
    ) {
      void releaseLeaseForImage(previousImageId)
    }

    activeImageCanEditRef.current = canEdit
    if (previousImageId === nextImageId) {
      return
    }

    activeImageKeyRef.current = nextImageId
  }, [canEdit, image, releaseLeaseForImage])

  React.useEffect(
    () => () => {
      const activeImageId = activeImageKeyRef.current
      if (
        !activeImageId ||
        !activeImageCanEditRef.current ||
        handledImageIdsRef.current.has(activeImageId)
      ) {
        return
      }
      void releaseLeaseForImage(activeImageId)
    },
    [releaseLeaseForImage],
  )

  const handleReload = React.useCallback(async () => {
    if (injectedSession) {
      return
    }
    if (requestedImageId) {
      await requestDetectionImage(requestedImageId)
      return
    }
    await requestNextImage()
  }, [
    injectedSession,
    requestDetectionImage,
    requestNextImage,
    requestedImageId,
  ])

  const markImageHandled = React.useCallback((imageId: string) => {
    handledImageIdsRef.current.add(imageId)
  }, [])

  const unmarkImageHandled = React.useCallback((imageId: string) => {
    handledImageIdsRef.current.delete(imageId)
  }, [])

  const handleNextImage = React.useCallback(
    async (selectedImageId: string | null, editable: boolean) => {
      if (injectedSession) {
        return
      }
      if (
        selectedImageId &&
        editable &&
        !handledImageIdsRef.current.has(selectedImageId)
      ) {
        const released = await releaseLeaseForImage(selectedImageId)
        if (released) {
          handledImageIdsRef.current.add(selectedImageId)
        }
      }
      await requestNextImage()
    },
    [injectedSession, releaseLeaseForImage, requestNextImage],
  )

  return {
    canEdit,
    error,
    handleNextImage,
    handleReload,
    image,
    leaseLock,
    loading,
    loadingImage,
    loadingNext,
    markImageHandled,
    noItems,
    releaseLeaseForImage,
    requestNextImage,
    requestedImageId,
    returnedFeedbackCount,
    reviewFeedback,
    statusMessage,
    unmarkImageHandled,
  }
}
